import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Wifi,
  ChevronRight,
  ChevronDown,
  Search,
  Home,
  Radio,
  Scan,
  Sliders,
  Calendar,
  Cpu,
  FileText,
  CreditCard,
  Users,
  HelpCircle,
  BookOpen,
  Play,
  AlertTriangle,
  CheckCircle2,
  ArrowLeft,
  Menu,
  X,
  Bell,
  Smartphone,
  Brain,
  Sparkles,
  Upload,
  Download,
  FileUp,
  MapPin,
  Building2,
  Zap,
  Shield,
  TrendingUp,
  ExternalLink,
  Star,
  Globe,
  Calculator,
  Map,
  Usb,
  Box,
  Layers,
  Server,
  WifiOff,
  Monitor,
  BarChart3,
  Clock,
  Target,
} from "lucide-react";

export default function UserManualPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSection, setActiveSection] = useState("inicio");
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Scroll to top when section changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSection]);

  const sections = [
    { id: "inicio", icon: Home, title: "Inicio", badge: null },
    { id: "primeros-pasos", icon: Play, title: "Primeros Pasos", badge: null },
    { id: "equipos", icon: Cpu, title: "Equipos Soportados", badge: null },
    { id: "sistemas-inear", icon: Radio, title: "Sistemas In-Ear", badge: null },
    { id: "racks-red", icon: Box, title: "Racks y Control de Red", badge: "NUEVO" },
    { id: "agente-local", icon: Server, title: "Agente Local", badge: "NUEVO" },
    { id: "frecuencias", icon: Sliders, title: "Asignacion de Frecuencias", badge: null },
    { id: "eventos-grupos", icon: Layers, title: "Eventos y Grupos", badge: null },
    { id: "ia-prediccion", icon: Brain, title: "IA Prediccion RF", badge: null },
    { id: "venues", icon: Building2, title: "Venues y Favoritos", badge: null },
    { id: "importar-wwb", icon: FileUp, title: "Importar WWB", badge: null },
    { id: "escaneo-rf", icon: Scan, title: "Escaneo RF", badge: null },
    { id: "modo-coordinacion", icon: BarChart3, title: "Modo Coordinación", badge: "NUEVO" },
    { id: "mapa-cobertura", icon: Map, title: "Mapa de Cobertura RF", badge: null },
    { id: "relacart-usb", icon: Usb, title: "Hardware USB (Relacart/tinySA)", badge: null },
    { id: "intermodulacion", icon: Calculator, title: "Calculadora IMD", badge: null },
    { id: "hardware", icon: Cpu, title: "Integracion Hardware", badge: null },
    { id: "reportes", icon: FileText, title: "Reportes PDF", badge: null },
    { id: "notificaciones", icon: Bell, title: "Notificaciones", badge: null },
    { id: "suscripciones", icon: CreditCard, title: "Suscripciones", badge: null },
    { id: "faq", icon: HelpCircle, title: "Preguntas Frecuentes", badge: null },
  ];

  const supportedEquipment = [
    { 
      brand: "Anleon", 
      model: "S3D", 
      bands: "560-674 MHz", 
      maxSystems: 8, 
      spacing: "0.5 MHz",
      features: ["8 grupos", "20 canales/grupo", "FM Stereo"]
    },
    { 
      brand: "Anleon", 
      model: "S3", 
      bands: "518-866 MHz (5 bandas)", 
      maxSystems: 8, 
      spacing: "0.4 MHz",
      features: ["8 grupos", "10 canales/grupo", "Modo Mono/Estereo"]
    },
    { 
      brand: "Sennheiser", 
      model: "SR 2050 IEM", 
      bands: "470-865 MHz (BW/GW/AW)", 
      maxSystems: 16, 
      spacing: "0.025 MHz",
      features: ["HDX Audio", "Multi-banda", "Profesional"]
    },
    { 
      brand: "Shure", 
      model: "PSM 1000", 
      bands: "470-668 MHz (G10/K1/J8A)", 
      maxSystems: 20, 
      spacing: "0.025 MHz",
      features: ["Audio de referencia", "CueMode", "Premium"]
    },
    { 
      brand: "Relacart", 
      model: "PM-320T", 
      bands: "554-936 MHz (5 bandas)", 
      maxSystems: 16, 
      spacing: "0.1 MHz",
      features: ["317 canales", "IrDA Sync", "U.Discover compatible"]
    },
    { 
      brand: "Audio-Technica", 
      model: "M3 / M2", 
      bands: "470-866 MHz (5 bandas)", 
      maxSystems: 8, 
      spacing: "0.4 MHz",
      features: ["IrDA Sync", "Portatil", "Buena relacion calidad/precio"]
    },
    { 
      brand: "LD Systems", 
      model: "MEI 1000 G2", 
      bands: "584-832 MHz", 
      maxSystems: 4, 
      spacing: "0.25 MHz",
      features: ["Economico", "Facil uso", "Entry Level"]
    },
  ];

  const faqs = [
    {
      question: "Que navegadores son compatibles?",
      answer: "AVX RF PRO funciona mejor en Google Chrome y Microsoft Edge. Para la conexion de hardware USB (tinySA, RF Explorer), es necesario usar Chrome o Edge ya que utilizan la Web Serial API."
    },
    {
      question: "Que marcas de equipos IEM son compatibles?",
      answer: "Actualmente soportamos Anleon (S3D, S3), Sennheiser SR 2050 IEM, Shure PSM 1000, Audio-Technica (M3, M2) y LD Systems MEI 1000 G2. La plataforma se adapta automaticamente segun el equipo seleccionado."
    },
    {
      question: "Como funciona la IA de Prediccion RF?",
      answer: "El sistema utiliza un enfoque hibrido: analisis local instantaneo (<100ms) para detectar interferencias de TV y bandas LTE, mas analisis profundo con inteligencia artificial que evalua patrones complejos y genera recomendaciones personalizadas."
    },
    {
      question: "Como importo sistemas desde Wireless Workbench?",
      answer: "En la pagina de Sistemas In-Ear, haz clic en el boton 'Importar WWB'. Selecciona tu archivo CSV exportado de Shure Wireless Workbench. El sistema detectara automaticamente los sistemas y te permitira elegir cuales importar."
    },
    {
      question: "Como conecto mi analizador de espectro?",
      answer: "Ve a la seccion Hardware, selecciona tu dispositivo (tinySA, RF Explorer, o Relacart), haz clic en 'Conectar' y selecciona el puerto USB correspondiente. Asegurate de abrir la aplicacion en una nueva pestana (no en iframe)."
    },
    {
      question: "Que significa cada color en los indicadores?",
      answer: "Verde: senal optima, sin interferencias. Amarillo: senal aceptable, posible interferencia menor. Rojo: interferencia detectada o senal debil, requiere atencion."
    },
    {
      question: "Como funciona la asignacion de frecuencias por equipo?",
      answer: "Al seleccionar un equipo en la pagina de Frecuencias, el sistema ajusta automaticamente las bandas disponibles, el espaciado minimo y la cantidad maxima de sistemas recomendados segun las especificaciones del fabricante."
    },
    {
      question: "Puedo usar la plataforma sin conexion a internet?",
      answer: "Si, AVX RF PRO es una PWA (Progressive Web App). Puedes instalarla en tu dispositivo y usar la funcion Offline para trabajar sin conexion durante eventos en vivo."
    },
    {
      question: "Como funcionan los Venues (configuraciones favoritas)?",
      answer: "Los Venues te permiten guardar configuraciones de frecuencias por ubicacion. Puedes exportarlas como JSON para respaldo o compartir con otros usuarios, e importar configuraciones existentes."
    },
    {
      question: "Como genero un reporte PDF?",
      answer: "Ve a la seccion Reportes, selecciona un evento o usa 'Reporte Rapido' para generar un PDF con toda la informacion de tus sistemas, frecuencias y configuraciones actuales."
    },
  ];

  const filteredSections = sections.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderContent = () => {
    switch (activeSection) {
      case "inicio":
        return (
          <div className="space-y-8">
            {/* Hero Section */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#111111] to-[#1a1a1a] border border-white/[0.08] p-8">
              <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/5 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center">
                    <Wifi className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-white">AVX RF PRO</h1>
                    <p className="text-sm text-green-400">Manual de Usuario v2.1</p>
                  </div>
                </div>
                <p className="text-gray-400 text-lg max-w-2xl">
                  Plataforma profesional multi-marca para gestion de frecuencias RF en sistemas de monitoreo In-Ear. 
                  Compatible con Anleon, Sennheiser, Shure, Audio-Technica y LD Systems.
                </p>
                
                {/* Hero Image */}
                <div className="mt-6 rounded-xl overflow-hidden border border-white/10">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/7574e1e4-298d-4d08-af4e-47445287433e/images/616d53646a707bcf40ee3b18052915035f24ac68096fb77fb9405244038072c8.png"
                    alt="AVX RF PRO - Coordinación de Frecuencias RF Profesional"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
            
            {/* Getting Started CTA */}
            <Card className="bg-green-900/20 border-green-700/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-600/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Play className="w-6 h-6 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-green-400 mb-2">Nuevo en AVX RF PRO?</h3>
                    <p className="text-gray-300 mb-4">
                      Te recomendamos comenzar con la seccion "Primeros Pasos" para familiarizarte con la plataforma.
                    </p>
                    <Button 
                      onClick={() => setActiveSection("primeros-pasos")}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Comenzar Tutorial
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* New Features Highlight */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                Nuevas Funcionalidades
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Card 
                  className="bg-[#111111] border-purple-500/30 hover:border-purple-500/50 transition cursor-pointer"
                  onClick={() => setActiveSection("ia-prediccion")}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-purple-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Brain className="w-6 h-6 text-purple-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-white">IA Prediccion RF</h3>
                          <Badge className="bg-purple-500/20 text-purple-400 text-[10px]">NUEVO</Badge>
                        </div>
                        <p className="text-sm text-gray-400">
                          Sistema hibrido de analisis con IA para detectar interferencias y predecir problemas RF.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-[#111111] border-blue-500/30 hover:border-blue-500/50 transition cursor-pointer"
                  onClick={() => setActiveSection("importar-wwb")}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FileUp className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-white">Importar Wireless Workbench</h3>
                          <Badge className="bg-blue-500/20 text-blue-400 text-[10px]">NUEVO</Badge>
                        </div>
                        <p className="text-sm text-gray-400">
                          Importa tus configuraciones desde Shure WWB con un solo clic.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-[#111111] border-orange-500/30 hover:border-orange-500/50 transition cursor-pointer"
                  onClick={() => setActiveSection("venues")}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-orange-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-orange-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-white">Venues y Favoritos</h3>
                          <Badge className="bg-orange-500/20 text-orange-400 text-[10px]">NUEVO</Badge>
                        </div>
                        <p className="text-sm text-gray-400">
                          Guarda y reutiliza configuraciones de frecuencias por ubicacion.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-[#111111] border-cyan-500/30 hover:border-cyan-500/50 transition cursor-pointer"
                  onClick={() => setActiveSection("intermodulacion")}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-cyan-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Calculator className="w-6 h-6 text-cyan-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-white">Calculadora IMD</h3>
                        </div>
                        <p className="text-sm text-gray-400">
                          Analiza productos de intermodulacion de 3er y 5to orden.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Quick Access Grid */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Acceso Rapido</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card 
                  className="bg-[#111111] border-white/[0.08] hover:border-green-500/30 transition cursor-pointer"
                  onClick={() => setActiveSection("equipos")}
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Cpu className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Equipos Soportados</h3>
                      <p className="text-xs text-gray-500">6 marcas IEM profesionales</p>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-[#111111] border-white/[0.08] hover:border-green-500/30 transition cursor-pointer"
                  onClick={() => setActiveSection("frecuencias")}
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Sliders className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Asignacion de Frecuencias</h3>
                      <p className="text-xs text-gray-500">Calculo inteligente por equipo</p>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-[#111111] border-white/[0.08] hover:border-green-500/30 transition cursor-pointer"
                  onClick={() => setActiveSection("hardware")}
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Cpu className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Hardware RF</h3>
                      <p className="text-xs text-gray-500">tinySA, RF Explorer, Relacart</p>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-[#111111] border-white/[0.08] hover:border-green-500/30 transition cursor-pointer"
                  onClick={() => setActiveSection("reportes")}
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Reportes PDF</h3>
                      <p className="text-xs text-gray-500">Documentacion profesional</p>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-[#111111] border-white/[0.08] hover:border-green-500/30 transition cursor-pointer"
                  onClick={() => setActiveSection("notificaciones")}
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Bell className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Notificaciones</h3>
                      <p className="text-xs text-gray-500">Alertas en tiempo real</p>
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="bg-[#111111] border-white/[0.08] hover:border-green-500/30 transition cursor-pointer"
                  onClick={() => setActiveSection("faq")}
                >
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <HelpCircle className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">FAQ</h3>
                      <p className="text-xs text-gray-500">Preguntas frecuentes</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Platform Features Summary */}
            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Caracteristicas de la Plataforma</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-900/30 rounded flex items-center justify-center flex-shrink-0">
                      <Globe className="w-4 h-4 text-green-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm">Multi-Idioma</p>
                      <p className="text-[10px] text-gray-500">Espanol / English</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-900/30 rounded flex items-center justify-center flex-shrink-0">
                      <Smartphone className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm">PWA Offline</p>
                      <p className="text-[10px] text-gray-500">Funciona sin internet</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-yellow-900/30 rounded flex items-center justify-center flex-shrink-0">
                      <Bell className="w-4 h-4 text-yellow-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm">Tiempo Real</p>
                      <p className="text-[10px] text-gray-500">WebSocket alerts</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-900/30 rounded flex items-center justify-center flex-shrink-0">
                      <Brain className="w-4 h-4 text-purple-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm">IA Integrada</p>
                      <p className="text-[10px] text-gray-500">Prediccion RF</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "ia-prediccion":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-purple-900/30 rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">IA Prediccion RF</h1>
                <Badge className="bg-purple-500/20 text-purple-400">NUEVO</Badge>
              </div>
            </div>
            <p className="text-gray-400">
              Sistema hibrido de inteligencia artificial para analisis y prediccion de interferencias en frecuencias RF.
            </p>

            {/* How it works */}
            <Card className="bg-gradient-to-br from-purple-900/20 to-[#111111] border-purple-700/30">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-purple-400 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Como Funciona
                </h3>
                <p className="text-gray-300">
                  AVX RF PRO utiliza un sistema <strong className="text-white">hibrido</strong> que combina dos tipos de analisis:
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-purple-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      <h4 className="font-semibold text-white">Analisis Local Instantaneo</h4>
                    </div>
                    <p className="text-sm text-gray-400 mb-3">
                      Respuesta en menos de 100ms. Detecta automaticamente:
                    </p>
                    <ul className="space-y-2 text-sm text-gray-400">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Interferencias de canales de TV chilenos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Bandas LTE/4G ocupadas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Conflictos de espaciado minimo</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Productos de intermodulacion</span>
                      </li>
                    </ul>
                  </div>

                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-purple-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Brain className="w-5 h-5 text-purple-400" />
                      <h4 className="font-semibold text-white">Analisis Profundo con IA</h4>
                    </div>
                    <p className="text-sm text-gray-400 mb-3">
                      Analisis avanzado con inteligencia artificial. Proporciona:
                    </p>
                    <ul className="space-y-2 text-sm text-gray-400">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                        <span>Evaluacion contextual por venue y evento</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                        <span>Recomendaciones de frecuencias alternativas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                        <span>Patrones de interferencia por hora/dia</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                        <span>Optimizacion de coordinacion RF</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Where to find it */}
            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Donde Encontrarlo</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-[#0A0A0A] rounded-lg">
                    <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Widget en Dashboard</h4>
                      <p className="text-sm text-gray-400">
                        Al ingresar al Dashboard veras el widget <strong className="text-purple-400">"Prediccion RF"</strong> que muestra 
                        el estado general de seguridad de tus sistemas. Se actualiza automaticamente.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-[#0A0A0A] rounded-lg">
                    <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Boton "Analizar Riesgos RF" en Frecuencias</h4>
                      <p className="text-sm text-gray-400">
                        Despues de calcular frecuencias en la pagina de Frecuencias, aparece el boton 
                        <strong className="text-purple-400"> "Analizar Riesgos RF"</strong>. Haz clic para obtener un 
                        analisis detallado de cada frecuencia calculada.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-[#0A0A0A] rounded-lg">
                    <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Analisis Profundo IA</h4>
                      <p className="text-sm text-gray-400">
                        Dentro del modal de resultados, haz clic en <strong className="text-purple-400">"Analisis IA"</strong> para 
                        obtener recomendaciones avanzadas con inteligencia artificial. Este analisis toma unos segundos adicionales.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Risk Levels */}
            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Niveles de Riesgo</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-4 p-3 bg-[#0A0A0A] rounded-lg">
                    <div className="w-12 h-12 bg-[#34C759]/20 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-[#34C759]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#34C759]">Bajo Riesgo (0-25%)</p>
                      <p className="text-sm text-gray-400">Frecuencia segura, sin conflictos detectados</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-[#0A0A0A] rounded-lg">
                    <div className="w-12 h-12 bg-[#FFCC00]/20 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-[#FFCC00]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#FFCC00]">Riesgo Medio (26-50%)</p>
                      <p className="text-sm text-gray-400">Posible interferencia menor, verificar antes de usar</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-[#0A0A0A] rounded-lg">
                    <div className="w-12 h-12 bg-[#FF9500]/20 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-[#FF9500]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#FF9500]">Riesgo Alto (51-75%)</p>
                      <p className="text-sm text-gray-400">Conflicto probable, considerar alternativa</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-[#0A0A0A] rounded-lg">
                    <div className="w-12 h-12 bg-[#FF3B30]/20 rounded-lg flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-[#FF3B30]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#FF3B30]">Critico (76-100%)</p>
                      <p className="text-sm text-gray-400">Interferencia confirmada, cambiar frecuencia</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "venues":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-orange-900/30 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Venues y Favoritos</h1>
                <Badge className="bg-orange-500/20 text-orange-400">NUEVO</Badge>
              </div>
            </div>
            <p className="text-gray-400">
              Guarda configuraciones de frecuencias por ubicacion para reutilizarlas facilmente.
            </p>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Que es un Venue?</h3>
                <p className="text-gray-400">
                  Un <strong className="text-orange-400">Venue</strong> es una configuracion guardada de frecuencias asociada a una 
                  ubicacion especifica (teatro, iglesia, estadio, etc.). Esto te permite:
                </p>
                <ul className="space-y-2 text-gray-400">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span>Reutilizar configuraciones probadas cuando vuelvas a un lugar</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span>Compartir configuraciones con otros tecnicos de tu equipo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                    <span>Hacer backup de tus configuraciones favoritas</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Crear un Venue</h3>
                <ol className="space-y-3 text-gray-400">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">1</span>
                    <span>Ve a <strong className="text-white">"Venues"</strong> en el menu lateral</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">2</span>
                    <span>Haz clic en <strong className="text-white">"Crear Venue"</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">3</span>
                    <span>Ingresa nombre, ubicacion y notas del venue</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">4</span>
                    <span>El venue se crea vacio. Puedes agregar frecuencias despues</span>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Guardar Frecuencias en un Venue</h3>
                <p className="text-gray-400 mb-4">Hay dos formas de guardar frecuencias:</p>
                
                <div className="space-y-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-orange-500/20">
                    <h4 className="font-semibold text-orange-400 mb-2">Opcion A: Desde la pagina de Frecuencias</h4>
                    <p className="text-sm text-gray-400">
                      Despues de calcular frecuencias, haz clic en el boton <strong className="text-white">"Guardar en Venue"</strong>. 
                      Puedes elegir un venue existente o crear uno nuevo con las frecuencias calculadas.
                    </p>
                  </div>
                  
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-orange-500/20">
                    <h4 className="font-semibold text-orange-400 mb-2">Opcion B: Desde la pagina de Venues</h4>
                    <p className="text-sm text-gray-400">
                      Abre un venue existente y usa el boton <strong className="text-white">"Agregar Frecuencias"</strong> para 
                      ingresar frecuencias manualmente o importarlas desde el calculo actual.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Download className="w-5 h-5 text-green-400" />
                  Exportar e Importar Venues
                </h3>
                <p className="text-gray-400">
                  Puedes exportar tus venues como archivos JSON para respaldo o para compartir con otros usuarios.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg">
                    <h4 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                      <Upload className="w-4 h-4" /> Exportar
                    </h4>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>Exportar un venue individual</li>
                      <li>Exportar todos los venues</li>
                      <li>Formato JSON con metadatos</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg">
                    <h4 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
                      <Download className="w-4 h-4" /> Importar
                    </h4>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>Importar archivo JSON</li>
                      <li>Preview antes de importar</li>
                      <li>Compatible con exports de AVX RF PRO</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-blue-900/20 border-blue-700/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-blue-400 mb-1">Limites por Plan</h4>
                    <p className="text-gray-300 text-sm">
                      <strong>Free:</strong> 3 venues | <strong>Pro:</strong> 15 venues | <strong>Enterprise:</strong> Ilimitados
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "importar-wwb":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-900/30 rounded-xl flex items-center justify-center">
                <FileUp className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Importar Wireless Workbench</h1>
                <Badge className="bg-blue-500/20 text-blue-400">NUEVO</Badge>
              </div>
            </div>
            <p className="text-gray-400">
              Importa tus configuraciones desde Shure Wireless Workbench (WWB) con archivos CSV.
            </p>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Como Exportar desde WWB</h3>
                <ol className="space-y-3 text-gray-400">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">1</span>
                    <span>Abre tu proyecto en <strong className="text-white">Shure Wireless Workbench</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">2</span>
                    <span>Ve a <strong className="text-white">File &gt; Export</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">3</span>
                    <span>Selecciona formato <strong className="text-white">CSV</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">4</span>
                    <span>Guarda el archivo en tu computador</span>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Como Importar en AVX RF PRO</h3>
                <ol className="space-y-3 text-gray-400">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">1</span>
                    <span>Ve a <strong className="text-white">"Sistemas In-Ear"</strong> en el menu</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">2</span>
                    <span>Haz clic en el boton <strong className="text-white">"Importar WWB"</strong> (icono purpura)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">3</span>
                    <span>Selecciona tu archivo CSV</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">4</span>
                    <span>Revisa el preview de sistemas detectados</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">5</span>
                    <span>Marca los sistemas que deseas importar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">6</span>
                    <span>Haz clic en <strong className="text-white">"Importar Seleccionados"</strong></span>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Formatos CSV Soportados</h3>
                <p className="text-gray-400">El importador detecta automaticamente estos formatos:</p>
                
                <div className="space-y-3">
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <h4 className="font-mono text-sm text-blue-400 mb-1">Formato con Headers</h4>
                    <code className="text-xs text-gray-500">Name,Frequency,Band,Model,Channel...</code>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <h4 className="font-mono text-sm text-blue-400 mb-1">Formato Simple</h4>
                    <code className="text-xs text-gray-500">Vocalist,608.125,G10,PSM1000</code>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-yellow-900/20 border-yellow-700/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-yellow-400 mb-1">Nota Importante</h4>
                    <p className="text-gray-300 text-sm">
                      Los sistemas importados se asignan automaticamente al equipo IEM mas compatible segun la frecuencia. 
                      Puedes editar el equipo manualmente despues de la importacion si es necesario.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "equipos":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white">Equipos Soportados</h1>
            <p className="text-gray-400">
              AVX RF PRO es compatible con las principales marcas de sistemas In-Ear Monitor del mercado. 
              La plataforma se adapta automaticamente segun el equipo seleccionado.
            </p>

            <div className="space-y-4">
              {supportedEquipment.map((eq) => (
                <Card key={`${eq.brand}-${eq.model}`} className="bg-[#111111] border-white/[0.08] hover:border-green-500/20 transition">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-green-900/30 rounded-lg flex items-center justify-center">
                            <Radio className="w-5 h-5 text-green-400" />
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold text-white">{eq.brand}</h3>
                            <p className="text-sm text-green-400">{eq.model}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Bandas</p>
                            <p className="text-sm text-white font-mono">{eq.bands}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Sistemas Max</p>
                            <p className="text-sm text-white font-mono">{eq.maxSystems}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Espaciado Min</p>
                            <p className="text-sm text-white font-mono">{eq.spacing}</p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {eq.features.map((feature, idx) => (
                            <Badge key={idx} variant="secondary" className="bg-white/5 text-gray-400 text-xs">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-blue-900/20 border-blue-700/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-blue-400 mb-1">Configuracion Automatica</h4>
                    <p className="text-gray-300 text-sm">
                      Al seleccionar un equipo en las secciones de Sistemas o Frecuencias, la plataforma 
                      ajusta automaticamente las bandas disponibles, espaciado minimo, grupos de canales y 
                      cantidad maxima de sistemas recomendados.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-green-400 mb-1">Tu equipo no esta listado?</h4>
                    <p className="text-gray-300 text-sm">
                      Contactanos a traves del formulario de contacto en la pagina principal para solicitar 
                      soporte para tu equipo IEM. Estamos constantemente agregando nuevos modelos.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "primeros-pasos":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white">Primeros Pasos</h1>
            <p className="text-gray-400">Guia rapida para comenzar a usar AVX RF PRO en 5 minutos.</p>

            <div className="space-y-4">
              <Card className="bg-[#111111] border-white/[0.08]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">1</div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Crear una cuenta</h3>
                      <p className="text-gray-400 mb-3">
                        Registrate en la plataforma con tu email y contrasena. El plan gratuito te permite gestionar hasta 6 sistemas In-Ear.
                      </p>
                      <ul className="list-disc list-inside text-gray-400 space-y-1 text-sm">
                        <li>Ve a la pagina de inicio y haz clic en "Comenzar Gratis"</li>
                        <li>Completa el formulario de registro</li>
                        <li>Inicia sesion con tus credenciales</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#111111] border-white/[0.08]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">2</div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Seleccionar tu equipo IEM</h3>
                      <p className="text-gray-400 mb-3">
                        Elige la marca y modelo de tu sistema In-Ear. La plataforma se adaptara automaticamente.
                      </p>
                      <ul className="list-disc list-inside text-gray-400 space-y-1 text-sm">
                        <li>Ve a "Sistemas In-Ear" en el menu lateral</li>
                        <li>Haz clic en "Agregar Sistema"</li>
                        <li>Selecciona tu equipo: Anleon, Sennheiser, Shure, etc.</li>
                        <li>El sistema mostrara las bandas y canales disponibles</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#111111] border-white/[0.08]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">3</div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Asignar frecuencias automaticamente</h3>
                      <p className="text-gray-400 mb-3">
                        Usa el algoritmo de asignacion para calcular frecuencias optimas segun tu equipo.
                      </p>
                      <ul className="list-disc list-inside text-gray-400 space-y-1 text-sm">
                        <li>Ve a "Frecuencias" en el menu</li>
                        <li>Selecciona tu marca de equipo (tarjetas en la parte superior)</li>
                        <li>Ajusta la cantidad de sistemas necesarios</li>
                        <li>Haz clic en "Calcular Frecuencias"</li>
                        <li>Analiza con IA para verificar riesgos</li>
                        <li>Importa las frecuencias a tus sistemas con un clic</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#111111] border-white/[0.08]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">4</div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Guardar en Venue (opcional)</h3>
                      <p className="text-gray-400 mb-3">
                        Guarda tu configuracion de frecuencias para reutilizarla en el futuro.
                      </p>
                      <ul className="list-disc list-inside text-gray-400 space-y-1 text-sm">
                        <li>Despues de calcular, haz clic en "Guardar en Venue"</li>
                        <li>Crea un nuevo venue o selecciona uno existente</li>
                        <li>La proxima vez que visites ese lugar, carga el venue</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#111111] border-white/[0.08]">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">5</div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">Generar reporte PDF</h3>
                      <p className="text-gray-400 mb-3">
                        Crea documentos profesionales con toda tu configuracion RF.
                      </p>
                      <ul className="list-disc list-inside text-gray-400 space-y-1 text-sm">
                        <li>Ve a "Reportes" en el menu</li>
                        <li>Selecciona un evento o usa "Reporte Rapido"</li>
                        <li>Descarga el PDF con visualizacion de espectro</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case "sistemas-inear":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-green-900/30 rounded-xl flex items-center justify-center">
                <Radio className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Sistemas In-Ear y Microfonos</h1>
                <Badge className="bg-green-500/20 text-green-400">ACTUALIZADO</Badge>
              </div>
            </div>
            <p className="text-gray-400">Gestion completa de equipos IEM y microfonos inalambricos multi-marca.</p>

            {/* New: Types and Search */}
            <Card className="bg-gradient-to-br from-green-900/20 to-[#111111] border-green-700/30">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-green-400 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Organizacion por Categorias
                </h3>
                
                <p className="text-gray-400">
                  Los equipos ahora se organizan en <strong className="text-white">secciones desplegables</strong> que puedes 
                  expandir o colapsar segun necesites.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-green-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Radio className="w-5 h-5 text-green-400" />
                      <h4 className="font-semibold text-green-400">Monitores In-Ear (IEM)</h4>
                    </div>
                    <p className="text-sm text-gray-400 mb-3">
                      Seccion con fondo verde para equipos de monitoreo personal:
                    </p>
                    <ul className="space-y-1 text-sm text-gray-400">
                      <li>• Anleon S3, S3D</li>
                      <li>• Sennheiser SR 2050 IEM</li>
                      <li>• Shure PSM series</li>
                      <li>• Relacart PM-320T</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-purple-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Radio className="w-5 h-5 text-purple-400" />
                      <h4 className="font-semibold text-purple-400">Microfonos Inalambricos</h4>
                    </div>
                    <p className="text-sm text-gray-400 mb-3">
                      Seccion con fondo morado para receptores de microfonos:
                    </p>
                    <ul className="space-y-1 text-sm text-gray-400">
                      <li>• Sennheiser EW-D, EW 100 G4</li>
                      <li>• Shure QLXD4, ULXD4</li>
                      <li>• Audio-Technica 3000 Series</li>
                    </ul>
                  </div>
                </div>
                
                <div className="p-4 bg-blue-900/10 rounded-lg border border-blue-500/20 mt-4">
                  <p className="text-sm text-blue-400">
                    <strong>Tip:</strong> Haz clic en el encabezado de cada seccion para expandir o colapsar los equipos. 
                    Esto te permite enfocarte solo en la categoria que necesitas.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Search className="w-5 h-5 text-green-400" />
                  Buscador y Filtros
                </h3>
                <p className="text-sm text-gray-400 mb-3">
                  Encuentra rapidamente tus sistemas:
                </p>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Barra de busqueda para filtrar por nombre</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Tabs para filtrar: <strong className="text-white">Todos</strong>, <strong className="text-green-400">IEM</strong>, <strong className="text-purple-400">Microfonos</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Contador de equipos y sistemas activos en cada categoria</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Crear un Sistema</h3>
                <ol className="space-y-3 text-gray-400">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">1</span>
                    <span>Haz clic en <strong className="text-white">"Agregar Sistema"</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">2</span>
                    <span>Ingresa un <strong className="text-white">nombre descriptivo</strong> (ej: "Vocalista Principal")</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">3</span>
                    <span>Selecciona el <strong className="text-white">tipo de sistema</strong>: In-Ear Monitor o Microfono Inalambrico</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">4</span>
                    <span>Selecciona el <strong className="text-white">equipo</strong>: Anleon S3D, Sennheiser SR 2050, Shure PSM 1000, etc.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">5</span>
                    <span>Si el equipo tiene multiples bandas (ej: Sennheiser), selecciona la <strong className="text-white">banda de frecuencia</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">6</span>
                    <span>Ingresa la <strong className="text-white">frecuencia</strong> en MHz y selecciona <strong className="text-white">grupo y canal</strong></span>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Filtrar Sistemas</h3>
                <p className="text-gray-400 mb-4">
                  Usa los tabs en la parte superior para filtrar por tipo:
                </p>
                <div className="flex flex-wrap gap-3">
                  <div className="px-4 py-2 bg-green-900/30 rounded-lg border border-green-500/30">
                    <span className="text-green-400 font-medium">Todos</span>
                    <p className="text-xs text-gray-500">Ver todos los sistemas</p>
                  </div>
                  <div className="px-4 py-2 bg-green-900/30 rounded-lg border border-green-500/30">
                    <span className="text-green-400 font-medium">IEM</span>
                    <p className="text-xs text-gray-500">Solo In-Ear Monitors</p>
                  </div>
                  <div className="px-4 py-2 bg-blue-900/30 rounded-lg border border-blue-500/30">
                    <span className="text-blue-400 font-medium">Microfonos</span>
                    <p className="text-xs text-gray-500">Solo microfonos inalambricos</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Indicadores de Estado</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1.5 bg-green-500/20 text-green-400 text-sm rounded-full font-medium">ESTABLE</span>
                    <span className="text-gray-400">Senal optima, sin interferencias detectadas</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1.5 bg-yellow-500/20 text-yellow-400 text-sm rounded-full font-medium">RIESGO</span>
                    <span className="text-gray-400">Senal aceptable, posible interferencia menor</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1.5 bg-red-500/20 text-red-400 text-sm rounded-full font-medium">INTERFERENCIA</span>
                    <span className="text-gray-400">Problema detectado, requiere atencion inmediata</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-blue-900/20 border-blue-700/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-blue-400 mb-1">Tip: Coordinacion RF</h4>
                    <p className="text-gray-300 text-sm">
                      Aunque IEM y microfonos son tipos diferentes, ambos comparten el espectro RF. 
                      Asegurate de coordinar las frecuencias de todos tus sistemas para evitar interferencias.
                      Usa la <strong>Calculadora IMD</strong> para verificar productos de intermodulacion.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "racks-red":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-blue-900/30 rounded-xl flex items-center justify-center">
                <Box className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Racks y Control de Red</h1>
                <Badge className="bg-blue-500/20 text-blue-400">NUEVO</Badge>
              </div>
            </div>
            <p className="text-gray-400">Organiza tus sistemas en racks fisicos y controla equipos conectados a la red.</p>

            {/* Vista de Racks */}
            <Card className="bg-gradient-to-br from-blue-900/20 to-[#111111] border-blue-700/30">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-blue-400 flex items-center gap-2">
                  <Box className="w-5 h-5" />
                  Vista por Racks
                </h3>
                
                <p className="text-gray-400">
                  Organiza visualmente tus sistemas como racks fisicos de equipos. Cada rack representa 
                  una unidad de montaje con slots para receptores.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-blue-500/30">
                    <h4 className="font-semibold text-white mb-3">Crear Rack</h4>
                    <ol className="space-y-2 text-sm text-gray-400">
                      <li>1. Ve a <strong className="text-white">Sistemas → Racks</strong></li>
                      <li>2. Clic en <strong className="text-green-400">Crear Rack</strong></li>
                      <li>3. Asigna nombre, ubicacion y numero de slots (4, 6, 8 o 12)</li>
                      <li>4. Opcionalmente asocia a un Venue o Evento</li>
                    </ol>
                  </div>

                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-green-500/30">
                    <h4 className="font-semibold text-white mb-3">Asignar Sistemas</h4>
                    <ul className="space-y-2 text-sm text-gray-400">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span><strong className="text-white">Drag & Drop:</strong> Arrastra sistemas desde "Sin Asignar" a los slots</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span><strong className="text-white">Asignacion Masiva:</strong> Selecciona varios y asigna con un clic</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        <span><strong className="text-white">Slots Duales:</strong> Cada slot (1U) soporta 2 sistemas (A y B)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dispositivos de Red */}
            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Server className="w-5 h-5 text-blue-400" />
                  Dispositivos de Red
                </h3>
                <p className="text-sm text-gray-400 mb-3">
                  Registra tus receptores Sennheiser o Shure conectados a la red local para enviarles frecuencias directamente.
                </p>
                
                <div className="space-y-3">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-white/10">
                    <h4 className="font-semibold text-white mb-2">Agregar Dispositivo</h4>
                    <ol className="space-y-1 text-sm text-gray-400">
                      <li>1. Ve a <strong className="text-white">Hardware RF → Red</strong></li>
                      <li>2. Clic en <strong className="text-green-400">Agregar Dispositivo</strong></li>
                      <li>3. Ingresa: Nombre, IP, Marca (Sennheiser/Shure), Modelo y Canales</li>
                    </ol>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="p-3 bg-[#00A0E3]/10 rounded-lg border border-[#00A0E3]/30">
                      <p className="text-sm font-medium text-[#00A0E3]">Sennheiser</p>
                      <p className="text-xs text-gray-400">EM 2050, EM 6000, EW-D EM, SR 2050 IEM</p>
                    </div>
                    <div className="p-3 bg-[#E31837]/10 rounded-lg border border-[#E31837]/30">
                      <p className="text-sm font-medium text-[#E31837]">Shure</p>
                      <p className="text-xs text-gray-400">ULXD4, ULXD4D, QLXD4, AD4D, PSM 1000</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Verificar Conexion */}
            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-green-400" />
                  Verificar Conexion
                </h3>
                <p className="text-sm text-gray-400 mb-3">
                  Comprueba si tus dispositivos estan accesibles en la red antes de enviar frecuencias.
                </p>
                
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="p-3 bg-green-900/20 rounded-lg border border-green-500/30 text-center">
                    <Wifi className="w-6 h-6 text-green-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-green-400">En Linea</p>
                    <p className="text-xs text-gray-400">Dispositivo responde</p>
                  </div>
                  <div className="p-3 bg-red-900/20 rounded-lg border border-red-500/30 text-center">
                    <WifiOff className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-red-400">Sin Conexion</p>
                    <p className="text-xs text-gray-400">No responde al ping</p>
                  </div>
                  <div className="p-3 bg-gray-900/20 rounded-lg border border-gray-500/30 text-center">
                    <Wifi className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-400">Desconocido</p>
                    <p className="text-xs text-gray-400">No verificado aun</p>
                  </div>
                </div>
                
                <div className="p-4 bg-blue-900/10 rounded-lg border border-blue-500/20 mt-4">
                  <p className="text-sm text-blue-400">
                    <strong>Tip:</strong> Usa el boton <strong className="text-white">"Verificar Todos"</strong> para 
                    comprobar la conexion de todos los dispositivos a la vez.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Push to Network */}
            <Card className="bg-gradient-to-br from-purple-900/20 to-[#111111] border-purple-700/30">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-purple-400 flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  Enviar Frecuencias a Equipos (Push)
                </h3>
                
                <p className="text-gray-400">
                  Envia las frecuencias configuradas en un rack directamente a los receptores fisicos conectados en red.
                </p>
                
                <div className="space-y-3">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-purple-500/30">
                    <h4 className="font-semibold text-white mb-3">Como Usar Push to Network</h4>
                    <ol className="space-y-2 text-sm text-gray-400">
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">1</span>
                        <span>Asegura que el rack tenga sistemas con frecuencias asignadas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">2</span>
                        <span>Clic en el boton <strong className="text-blue-400">Push</strong> en la cabecera del rack</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">3</span>
                        <span>Selecciona los dispositivos de red destino (verifica que esten en linea)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">4</span>
                        <span>Clic en <strong className="text-blue-400">Enviar Frecuencias</strong></span>
                      </li>
                    </ol>
                  </div>
                  
                  <div className="p-4 bg-yellow-900/10 rounded-lg border border-yellow-500/20">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-yellow-400">Importante</p>
                        <p className="text-gray-400">
                          Antes de enviar frecuencias, asegurate de que los transmisores (bodypacks) esten apagados 
                          para evitar interferencias durante el cambio de configuracion.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "agente-local":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-green-900/30 rounded-xl flex items-center justify-center">
                <Server className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Agente Local</h1>
                <Badge className="bg-green-500/20 text-green-400">NUEVO</Badge>
              </div>
            </div>
            <p className="text-gray-400">
              El Agente Local es una aplicacion que se instala en tu computadora y permite que AVX RF PRO 
              detecte y controle equipos Shure y Sennheiser conectados a tu red local.
            </p>

            {/* Por que es necesario */}
            <Card className="bg-gradient-to-br from-blue-900/20 to-[#111111] border-blue-700/30">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-blue-400 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5" />
                  ¿Por que necesito el Agente Local?
                </h3>
                <p className="text-gray-400">
                  AVX RF PRO funciona en la nube, pero tus equipos Shure y Sennheiser estan en tu red local 
                  (ej: 192.168.1.x). El servidor en la nube no puede acceder directamente a tu red local por seguridad.
                </p>
                <p className="text-gray-400">
                  El Agente Local actua como <strong className="text-white">puente</strong> entre tu red local y la nube:
                </p>
                <div className="flex items-center justify-center gap-4 py-4">
                  <div className="text-center p-3 bg-[#0A0A0A] rounded-lg">
                    <Server className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">Tu PC con Agente</p>
                  </div>
                  <div className="text-green-400">→</div>
                  <div className="text-center p-3 bg-[#0A0A0A] rounded-lg">
                    <Wifi className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">Equipos en red local</p>
                  </div>
                  <div className="text-green-400">→</div>
                  <div className="text-center p-3 bg-[#0A0A0A] rounded-lg">
                    <Globe className="w-8 h-8 text-green-400 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">AVX RF PRO (nube)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Instalacion */}
            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Download className="w-5 h-5 text-green-400" />
                  Instalacion (1 minuto)
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[#00A4EF]/30">
                    <div className="flex items-center gap-2 mb-3">
                      <svg viewBox="0 0 24 24" className="w-6 h-6 text-[#00A4EF]" fill="currentColor">
                        <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/>
                      </svg>
                      <h4 className="font-semibold text-white">Windows</h4>
                    </div>
                    <ol className="space-y-2 text-sm text-gray-400">
                      <li>1. Ve a <strong className="text-white">Hardware RF → Control RF en Red</strong></li>
                      <li>2. Clic en <strong className="text-green-400">Descargar Agente</strong></li>
                      <li>3. Descarga el archivo <code className="text-blue-400">.hta</code></li>
                      <li>4. Doble clic para ejecutar el instalador</li>
                      <li>5. Clic en <strong className="text-green-400">Instalar Agente</strong></li>
                    </ol>
                  </div>

                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[#A2AAAD]/30">
                    <div className="flex items-center gap-2 mb-3">
                      <svg viewBox="0 0 24 24" className="w-6 h-6 text-[#A2AAAD]" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      <h4 className="font-semibold text-white">macOS</h4>
                    </div>
                    <ol className="space-y-2 text-sm text-gray-400">
                      <li>1. Ve a <strong className="text-white">Hardware RF → Control RF en Red</strong></li>
                      <li>2. Clic en <strong className="text-green-400">Descargar Agente</strong></li>
                      <li>3. Descarga el archivo <code className="text-blue-400">.zip</code></li>
                      <li>4. Descomprime y doble clic en <code className="text-blue-400">.command</code></li>
                      <li>5. Si pide permisos: clic derecho → Abrir</li>
                    </ol>
                  </div>
                </div>
                
                <div className="p-4 bg-green-900/10 rounded-lg border border-green-500/20 mt-4">
                  <p className="text-sm text-green-400">
                    <strong>Inicio Automatico:</strong> En macOS, el agente se configura para iniciarse automaticamente 
                    cada vez que enciendas tu Mac. En Windows, se crea un acceso directo en el escritorio.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Equipos Soportados */}
            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Radio className="w-5 h-5 text-blue-400" />
                  Equipos Soportados
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#E31837]/10 rounded-lg border border-[#E31837]/30">
                    <p className="font-medium text-[#E31837] mb-2">Shure (Soporte Completo)</p>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• AD4Q, AD4D (Axient Digital)</li>
                      <li>• ULXD4, ULXD4D, ULXD4Q</li>
                      <li>• QLXD4</li>
                      <li className="text-green-400 text-xs mt-2">✓ Discovery, Telemetria, Cambio de Frecuencia</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-[#00A0E3]/10 rounded-lg border border-[#00A0E3]/30">
                    <p className="font-medium text-[#00A0E3] mb-2">Sennheiser (Soporte Basico)</p>
                    <ul className="text-sm text-gray-400 space-y-1">
                      <li>• EM 2050, EM 6000</li>
                      <li>• EW-D, EW-DX</li>
                      <li className="text-yellow-400 text-xs mt-2">⚠ Solo Discovery y Status (protocolo propietario)</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Panel de Agentes */}
            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-blue-400" />
                  Panel de Agentes
                </h3>
                <p className="text-gray-400">
                  En <strong className="text-white">Hardware RF → Control RF en Red</strong> puedes ver todos los agentes registrados:
                </p>
                
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="p-3 bg-green-900/20 rounded-lg border border-green-500/30 text-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-2 animate-pulse" />
                    <p className="text-sm font-medium text-green-400">Online</p>
                    <p className="text-xs text-gray-400">Agente corriendo</p>
                  </div>
                  <div className="p-3 bg-red-900/20 rounded-lg border border-red-500/30 text-center">
                    <div className="w-3 h-3 bg-red-500 rounded-full mx-auto mb-2" />
                    <p className="text-sm font-medium text-red-400">Offline</p>
                    <p className="text-xs text-gray-400">Agente detenido</p>
                  </div>
                  <div className="p-3 bg-gray-900/20 rounded-lg border border-gray-500/30 text-center">
                    <p className="text-sm font-medium text-gray-400">0 dispositivos</p>
                    <p className="text-xs text-gray-400">Sin equipos en red</p>
                  </div>
                </div>
                
                <div className="p-4 bg-blue-900/10 rounded-lg border border-blue-500/20 mt-4">
                  <p className="text-sm text-blue-400">
                    <strong>Re-escanear Red:</strong> Usa el boton "Re-escanear Red" para que el agente 
                    busque nuevamente equipos en tu red local.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Troubleshooting */}
            <Card className="bg-yellow-900/20 border-yellow-700/50">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-yellow-400 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Solucion de Problemas
                </h3>
                
                <div className="space-y-3">
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <p className="font-medium text-white text-sm">El agente no se conecta</p>
                    <p className="text-xs text-gray-400">Verifica tu conexion a Internet. El agente necesita acceso a avxrfpro.com</p>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <p className="font-medium text-white text-sm">No encuentra mis equipos Shure/Sennheiser</p>
                    <p className="text-xs text-gray-400">Asegurate de que esten en la misma red (misma subnet). Los equipos deben tener IP asignada.</p>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <p className="font-medium text-white text-sm">Error "Token invalido" o "User not found"</p>
                    <p className="text-xs text-gray-400">Descarga el instalador nuevamente desde la web para obtener un token actualizado.</p>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <p className="font-medium text-white text-sm">Error SSL en macOS</p>
                    <p className="text-xs text-gray-400">Ejecuta: <code className="text-blue-400">pip3 install certifi</code> en Terminal y reinicia el agente.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "frecuencias":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white">Asignacion de Frecuencias</h1>
            <p className="text-gray-400">Calculo inteligente de frecuencias adaptado a cada marca de equipo.</p>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Como Usar</h3>
                <ol className="space-y-3 text-gray-400">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">1</span>
                    <span><strong className="text-white">Selecciona tu equipo:</strong> Haz clic en la tarjeta de tu marca (Anleon, Sennheiser, Shure, etc.)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">2</span>
                    <span><strong className="text-white">Selecciona la banda:</strong> Si tu equipo tiene multiples bandas (ej: Sennheiser BW, GW), elige la correcta</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">3</span>
                    <span><strong className="text-white">Ajusta la cantidad:</strong> Usa el slider para indicar cuantos sistemas necesitas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">4</span>
                    <span><strong className="text-white">Calcula:</strong> El sistema generara frecuencias optimas con el espaciado correcto</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">5</span>
                    <span><strong className="text-white">Analiza con IA:</strong> Usa "Analizar Riesgos RF" para verificar interferencias</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">6</span>
                    <span><strong className="text-white">Importa o Guarda:</strong> Crea sistemas automaticamente o guarda en un Venue</span>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-yellow-900/20 border-yellow-700/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-yellow-400 mb-1">Advertencia de Limite</h4>
                    <p className="text-gray-300 text-sm">
                      Si seleccionas mas sistemas de los recomendados para tu equipo, veras una advertencia amarilla. 
                      Esto indica posible riesgo de intermodulacion. Cada equipo tiene un limite optimo basado en sus especificaciones.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Persistencia de Calculos</h3>
                <p className="text-gray-400">
                  Los resultados de calculo de frecuencias se guardan automaticamente en tu navegador. Si cambias de pagina 
                  y vuelves a Frecuencias, tus calculos seguiran ahi. Para limpiar, usa el boton <strong className="text-white">"Limpiar Calculo"</strong>.
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case "intermodulacion":
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-cyan-900/30 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-cyan-400" />
              </div>
              <h1 className="text-3xl font-bold text-white">Calculadora de Intermodulacion</h1>
            </div>
            <p className="text-gray-400">
              Analiza productos de intermodulacion (IMD) de 3er y 5to orden para detectar conflictos potenciales.
            </p>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Que es la Intermodulacion?</h3>
                <p className="text-gray-400">
                  La intermodulacion ocurre cuando dos o mas frecuencias interactuan creando productos adicionales que pueden 
                  causar interferencias. Los productos de <strong className="text-cyan-400">3er orden (2A-B, 2B-A)</strong> son los mas 
                  problematicos en sistemas RF.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Como Usar</h3>
                <ol className="space-y-3 text-gray-400">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">1</span>
                    <span>Ve a <strong className="text-white">"Calculadora IMD"</strong> en el menu</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">2</span>
                    <span>Ingresa las frecuencias a analizar (una por linea o separadas por coma)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">3</span>
                    <span>Haz clic en <strong className="text-white">"Calcular IMD"</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">4</span>
                    <span>Revisa los productos de intermodulacion detectados</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-cyan-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">5</span>
                    <span>Los conflictos se marcan en <strong className="text-red-400">rojo</strong></span>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-cyan-900/20 border-cyan-700/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-cyan-400 mb-1">Tip Profesional</h4>
                    <p className="text-gray-300 text-sm">
                      Para evitar intermodulacion, asegurate de que ningun producto IMD caiga dentro de +/- 0.1 MHz de cualquiera 
                      de tus frecuencias activas. El algoritmo de asignacion de AVX RF PRO ya considera esto automaticamente.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "escaneo-rf":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white">Escaneo RF</h1>
            <p className="text-gray-400">Analisis del espectro de radiofrecuencia para detectar interferencias.</p>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Modos de Escaneo</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-green-500/20">
                    <h4 className="font-semibold text-green-400 mb-2">Escaneo Simulado</h4>
                    <p className="text-sm text-gray-400">
                      Genera datos de prueba para familiarizarte con la interfaz. 
                      Util para demostraciones y aprendizaje.
                    </p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-blue-500/20">
                    <h4 className="font-semibold text-blue-400 mb-2">Escaneo Hardware</h4>
                    <p className="text-sm text-gray-400">
                      Conecta tu analizador RF (tinySA, RF Explorer, Relacart) 
                      para obtener datos reales del espectro.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Interpretacion del Grafico</h3>
                <ul className="space-y-3 text-gray-400">
                  <li className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded bg-green-500"></div>
                    <span><strong className="text-green-400">Picos bajos:</strong> Frecuencias disponibles (ruido de fondo)</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded bg-yellow-500"></div>
                    <span><strong className="text-yellow-400">Picos medios:</strong> Posible senal debil, verificar antes de usar</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded bg-red-500"></div>
                    <span><strong className="text-red-400">Picos altos:</strong> Frecuencia ocupada, evitar uso</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Modo Zoom</h3>
                <p className="text-gray-400">
                  Usa los presets de zoom por marca para enfocarte en la banda especifica de tu equipo. 
                  Tambien puedes ajustar manualmente el rango de frecuencias con los controles deslizantes.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Badge className="bg-green-900/30 text-green-400">Anleon 560-674 MHz</Badge>
                  <Badge className="bg-blue-900/30 text-blue-400">Sennheiser 470-865 MHz</Badge>
                  <Badge className="bg-purple-900/30 text-purple-400">Shure 470-668 MHz</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "mapa-cobertura":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Map className="w-8 h-8 text-green-400" />
              Mapa de Cobertura RF
            </h1>
            <p className="text-gray-400">Visualiza la cobertura de tus transmisores en el venue en 2D o 3D.</p>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">¿Para que sirve?</h3>
                <p className="text-gray-400">
                  El mapa de cobertura te permite planificar visualmente donde colocar tus transmisores IEM 
                  para asegurar que todos los artistas tengan buena recepcion de senal en el escenario.
                </p>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-blue-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Layers className="w-5 h-5 text-blue-400" />
                      <h4 className="font-semibold text-blue-400">Vista 2D</h4>
                    </div>
                    <p className="text-sm text-gray-400">
                      Vista plana tradicional con zoom y pan. Ideal para planificacion rapida.
                    </p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-purple-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Box className="w-5 h-5 text-purple-400" />
                      <h4 className="font-semibold text-purple-400">Vista 3D</h4>
                    </div>
                    <p className="text-sm text-gray-400">
                      Vista tridimensional rotable. Visualiza transmisores con antenas y zonas de cobertura.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Como usar el mapa</h3>
                <ol className="space-y-3 text-gray-400 list-decimal list-inside">
                  <li><strong className="text-white">Cargar plano:</strong> Haz clic en "Cargar Plano" y sube una imagen de tu venue (JPG/PNG)</li>
                  <li><strong className="text-white">Agregar TX:</strong> Haz clic en "+ Agregar TX", luego clic en el mapa donde quieres el transmisor</li>
                  <li><strong className="text-white">Configurar:</strong> Dale nombre, selecciona potencia (Alta/Media/Baja)</li>
                  <li><strong className="text-white">Visualizar:</strong> El circulo de color muestra el area de cobertura</li>
                  <li><strong className="text-white">Cambiar vista:</strong> Usa el toggle 2D/3D en la barra superior</li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Controles 3D</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <p className="text-green-400 font-mono text-sm">Click + Arrastrar</p>
                    <p className="text-gray-400 text-sm">Rotar la vista</p>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <p className="text-green-400 font-mono text-sm">Scroll</p>
                    <p className="text-gray-400 text-sm">Zoom in/out</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Leyenda de Potencia</h3>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-green-500"></div>
                    <span className="text-gray-400">Alta (30mW) - Mayor cobertura</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                    <span className="text-gray-400">Media (10mW)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                    <span className="text-gray-400">Baja (5mW) - Menor cobertura</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "modo-coordinacion":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-green-400" />
              Modo Coordinación
            </h1>
            <p className="text-gray-400">
              Analiza el espectro RF durante un período de tiempo para encontrar las mejores frecuencias 
              para tus sistemas inalámbricos. Ideal para soundchecks y preparación de eventos.
            </p>

            <Card className="bg-gradient-to-r from-green-900/20 to-transparent border-green-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Target className="w-6 h-6 text-green-400 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">¿Qué hace el Modo Coordinación?</h3>
                    <ul className="space-y-2 text-gray-400">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                        <span><strong className="text-white">Escaneo continuo:</strong> Acumula datos del espectro durante el tiempo que necesites</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                        <span><strong className="text-white">Detección de picos:</strong> Marca automáticamente cuando aparecen interferencias</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                        <span><strong className="text-white">Análisis estadístico:</strong> Calcula promedios, máximos y varianza por frecuencia</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                        <span><strong className="text-white">Recomendaciones:</strong> Sugiere los mejores rangos de frecuencia para tus equipos</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Cómo usar el Modo Coordinación</h3>
                <ol className="space-y-3 text-gray-400 list-decimal list-inside">
                  <li><strong className="text-white">Conecta tu tinySA</strong> via USB desde la página Hardware</li>
                  <li><strong className="text-white">Inicia el escaneo</strong> con el botón "Iniciar Escaneo"</li>
                  <li><strong className="text-white">Activa Modo Coordinación</strong> haciendo clic en "Iniciar" en el panel verde</li>
                  <li><strong className="text-white">Espera mientras analiza</strong> - mínimo 2-5 minutos recomendado</li>
                  <li><strong className="text-white">Detén el análisis</strong> cuando tengas suficientes datos</li>
                  <li><strong className="text-white">Revisa los resultados</strong> y exporta el reporte si lo necesitas</li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Interpretación de Resultados</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-green-900/20 rounded-lg border border-green-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <h4 className="font-semibold text-green-400">Frecuencias Limpias</h4>
                    </div>
                    <p className="text-sm text-gray-400">
                      Bajo nivel de ruido y estables. <strong className="text-green-300">Ideales para tus sistemas.</strong>
                    </p>
                  </div>
                  <div className="p-4 bg-red-900/20 rounded-lg border border-red-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <h4 className="font-semibold text-red-400">Frecuencias Ocupadas</h4>
                    </div>
                    <p className="text-sm text-gray-400">
                      Alto nivel de ruido constante. <strong className="text-red-300">Evitar usar estas frecuencias.</strong>
                    </p>
                  </div>
                  <div className="p-4 bg-yellow-900/20 rounded-lg border border-yellow-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <h4 className="font-semibold text-yellow-400">Frecuencias Inestables</h4>
                    </div>
                    <p className="text-sm text-gray-400">
                      Picos intermitentes. <strong className="text-yellow-300">Posibles interferencias periódicas.</strong>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-400" />
                  Tiempos de Análisis Recomendados
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <p className="text-blue-400 font-mono text-sm">2-5 minutos</p>
                    <p className="text-gray-400 text-sm">Análisis rápido / Verificación básica</p>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <p className="text-blue-400 font-mono text-sm">15-30 minutos</p>
                    <p className="text-gray-400 text-sm">Soundcheck / Preparación de evento</p>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <p className="text-blue-400 font-mono text-sm">1-2 horas</p>
                    <p className="text-gray-400 text-sm">Ensayo completo / Análisis detallado</p>
                  </div>
                  <div className="p-3 bg-[#0A0A0A] rounded-lg">
                    <p className="text-green-400 font-mono text-sm">Todo el show</p>
                    <p className="text-gray-400 text-sm">Historial completo del evento</p>
                  </div>
                </div>
                <p className="text-sm text-gray-500 italic">
                  Mayor tiempo de análisis = resultados más precisos
                </p>
              </CardContent>
            </Card>

            <Card className="bg-blue-900/20 border-blue-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Zap className="w-6 h-6 text-blue-400 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Tip Pro</h3>
                    <p className="text-gray-400">
                      Ejecuta el Modo Coordinación durante todo el soundcheck. Al finalizar, 
                      tendrás un mapa completo del espectro RF del venue que te ayudará a 
                      elegir las mejores frecuencias para el show.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "relacart-usb":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Usb className="w-8 h-8 text-green-400" />
              Conexion Hardware USB
            </h1>
            <p className="text-gray-400">Conecta tu analizador de espectro Relacart U.DISCOVER o tinySA directamente por USB.</p>

            <Card className="bg-gradient-to-r from-green-900/20 to-transparent border-green-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-6 h-6 text-green-400 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Dispositivos Soportados</h3>
                    <div className="grid md:grid-cols-2 gap-4 mt-3">
                      <div className="p-3 bg-green-900/20 rounded-lg border border-green-500/20">
                        <h4 className="font-semibold text-green-400">Relacart U.DISCOVER</h4>
                        <p className="text-sm text-gray-400">Via WebHID API</p>
                        <Badge className="mt-2 bg-green-900/50 text-green-300">240-960 MHz</Badge>
                      </div>
                      <div className="p-3 bg-purple-900/20 rounded-lg border border-purple-500/20">
                        <h4 className="font-semibold text-purple-400">tinySA / tinySA Ultra</h4>
                        <p className="text-sm text-gray-400">Via Web Serial API</p>
                        <Badge className="mt-2 bg-purple-900/50 text-purple-300">0.1-6000 MHz</Badge>
                      </div>
                    </div>
                    <p className="text-gray-400 mt-4">
                      Disponible solo en <strong className="text-blue-400">Google Chrome</strong> y <strong className="text-blue-400">Microsoft Edge</strong>.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-r from-blue-900/20 to-transparent border-blue-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Zap className="w-6 h-6 text-blue-400 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Conexion Persistente</h3>
                    <p className="text-gray-400">
                      Una vez conectado tu dispositivo, la conexion se <strong className="text-blue-400">mantiene activa</strong> aunque 
                      cambies de pagina. Puedes navegar por Dashboard, Sistemas, Frecuencias y volver a Hardware RF 
                      sin perder la conexion ni los datos del escaneo.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 text-sm font-bold">1</div>
                  Conectar Relacart DISCOVER
                </h3>
                <ol className="space-y-3 text-gray-400 list-decimal list-inside">
                  <li><strong className="text-white">Conecta el cable USB-C</strong> del Relacart DISCOVER a tu computador</li>
                  <li><strong className="text-white">Ve a Hardware RF</strong> en el menu lateral</li>
                  <li><strong className="text-white">Haz clic en "Conectar Relacart"</strong> (boton verde)</li>
                  <li><strong className="text-white">Selecciona el dispositivo</strong> en el dialogo del navegador</li>
                  <li><strong className="text-white">Haz clic en "Iniciar Escaneo"</strong> para comenzar a recibir datos</li>
                </ol>
                <div className="mt-4 p-3 bg-green-900/10 rounded-lg border border-green-500/20">
                  <p className="text-sm text-green-400">
                    <strong>Tip:</strong> Si el Relacart no aparece, haz clic en "Buscar otros dispositivos HID" 
                    para ver todos los dispositivos disponibles.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 text-sm font-bold">2</div>
                  Conectar tinySA
                </h3>
                <ol className="space-y-3 text-gray-400 list-decimal list-inside">
                  <li><strong className="text-white">Conecta el tinySA</strong> por USB a tu computador</li>
                  <li><strong className="text-white">Ve a Hardware RF</strong> en el menu lateral</li>
                  <li><strong className="text-white">Haz clic en "Conectar tinySA"</strong> (boton morado)</li>
                  <li><strong className="text-white">Selecciona el puerto serial</strong> (ej: COM3 o /dev/ttyUSB0)</li>
                  <li><strong className="text-white">Haz clic en "Iniciar Escaneo"</strong> para comenzar</li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Importar desde archivo CSV</h3>
                <p className="text-gray-400">
                  Si prefieres no usar USB directo, puedes exportar los datos desde el software 
                  U.DISCOVER o tinySA y luego importarlos:
                </p>
                <ol className="space-y-2 text-gray-400 list-decimal list-inside mt-3">
                  <li>Usa el software <strong className="text-white">U.DISCOVER</strong> para hacer el escaneo</li>
                  <li>Exporta los datos a CSV</li>
                  <li>En AVX RF PRO, ve a <strong className="text-white">Hardware RF</strong></li>
                  <li>Haz clic en <strong className="text-white">"Importar CSV (alternativa)"</strong></li>
                  <li>Selecciona tu archivo y listo</li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Formatos CSV soportados</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-green-500/20">
                    <h4 className="font-semibold text-green-400 mb-2">Escaneo de Espectro</h4>
                    <p className="text-sm text-gray-400 mb-2">Datos de frecuencia y nivel:</p>
                    <code className="text-xs bg-black/50 px-2 py-1 rounded text-green-300 block">
                      240725,-85<br/>
                      245075,-95<br/>
                      248700,-74
                    </code>
                    <p className="text-xs text-gray-500 mt-2">Frecuencia en kHz, nivel en dBm</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-purple-500/20">
                    <h4 className="font-semibold text-purple-400 mb-2">Coordinacion de Equipos</h4>
                    <p className="text-sm text-gray-400 mb-2">Exportado desde WWB/Relacart:</p>
                    <code className="text-xs bg-black/50 px-2 py-1 rounded text-purple-300 block">
                      freq,freqName,manufacturer...<br/>
                      594.575,MIC 01,Sennheiser...
                    </code>
                    <p className="text-xs text-gray-500 mt-2">Incluye equipos y nombres</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-yellow-900/20 border-yellow-500/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">Solucion de problemas</h3>
                    <ul className="space-y-2 text-gray-400 text-sm">
                      <li><strong className="text-yellow-400">No aparece el Relacart:</strong> Usa "Buscar otros dispositivos HID" o verifica el cable USB-C</li>
                      <li><strong className="text-yellow-400">No aparece el tinySA:</strong> Asegurate que el driver USB-Serial este instalado</li>
                      <li><strong className="text-yellow-400">Error de acceso denegado:</strong> Cierra otras aplicaciones que usen el puerto</li>
                      <li><strong className="text-yellow-400">No se reciben datos:</strong> Haz clic en "Iniciar Escaneo" despues de conectar</li>
                      <li><strong className="text-yellow-400">Conexion se pierde:</strong> La conexion ahora es persistente, pero si se desconecta fisicamente deberas reconectar</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "eventos-grupos":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white">Eventos y Grupos</h1>
            <p className="text-gray-400">Organiza tus configuraciones RF por evento y agrupa sistemas por funcion.</p>

            {/* Hero Image */}
            <div className="rounded-xl overflow-hidden border border-white/10 bg-[#0A0A0A]">
              <div className="p-4 bg-gradient-to-r from-green-500/10 to-blue-500/10">
                <div className="flex items-center gap-3">
                  <Layers className="w-8 h-8 text-green-400" />
                  <div>
                    <h3 className="text-lg font-semibold text-white">Sistema de Grupos</h3>
                    <p className="text-sm text-gray-400">Organiza tus sistemas IEM en grupos logicos dentro de cada evento</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Crear un Evento</h3>
                <ol className="space-y-3 text-gray-400">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">1</span>
                    <span>Ve a <strong className="text-white">Eventos</strong> en el menu lateral</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">2</span>
                    <span>Haz clic en <strong className="text-white">"Nuevo Evento"</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">3</span>
                    <span>Completa: <strong className="text-white">Nombre</strong>, <strong className="text-white">Ubicacion</strong>, <strong className="text-white">Fecha</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">4</span>
                    <span>Agrega notas opcionales sobre el evento</span>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-green-900/20 border-green-700/50">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                    <Layers className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-green-400">Crear Grupos dentro del Evento</h3>
                </div>
                <p className="text-gray-300 mb-4">
                  Los grupos te permiten organizar tus sistemas segun su funcion o ubicacion en el escenario.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border-l-4 border-orange-500">
                    <h4 className="font-semibold text-orange-400 mb-2">Banda Principal</h4>
                    <p className="text-sm text-gray-400">Vocalista, Guitarrista, Bajista, Baterista</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border-l-4 border-blue-500">
                    <h4 className="font-semibold text-blue-400 mb-2">Coros / Backing</h4>
                    <p className="text-sm text-gray-400">Coristas, Musicos de apoyo</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border-l-4 border-purple-500">
                    <h4 className="font-semibold text-purple-400 mb-2">Staff Tecnico</h4>
                    <p className="text-sm text-gray-400">Director musical, Stage manager</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border-l-4 border-red-500">
                    <h4 className="font-semibold text-red-400 mb-2">Microfonos</h4>
                    <p className="text-sm text-gray-400">Handhelds, Lavaliers, Diademas</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Como Crear un Grupo</h3>
                <ol className="space-y-3 text-gray-400">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">1</span>
                    <span>Abre el evento haciendo clic en <strong className="text-white">"Gestionar Grupos"</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">2</span>
                    <span>Haz clic en <strong className="text-white">"Nuevo Grupo"</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">3</span>
                    <span>Ingresa el <strong className="text-white">nombre</strong> del grupo (ej: "Banda Principal")</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">4</span>
                    <span>Selecciona un <strong className="text-white">color</strong> para identificarlo visualmente</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">5</span>
                    <span>Opcionalmente agrega una <strong className="text-white">descripcion</strong></span>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Asignar Sistemas a Grupos</h3>
                <p className="text-gray-400 mb-4">
                  Una vez creado el grupo, puedes asignar sistemas de dos formas:
                </p>
                
                <div className="space-y-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg">
                    <h4 className="font-semibold text-green-400 mb-2">Metodo Rapido</h4>
                    <p className="text-sm text-gray-400">
                      En la lista de <strong className="text-white">"Sistemas Disponibles"</strong>, cada sistema muestra 
                      botones con el nombre de tus grupos. Haz clic en <strong className="text-white">"+ [Nombre del Grupo]"</strong> 
                      para agregarlo instantaneamente.
                    </p>
                  </div>
                  
                  <div className="p-4 bg-[#0A0A0A] rounded-lg">
                    <h4 className="font-semibold text-blue-400 mb-2">Remover de Grupo</h4>
                    <p className="text-sm text-gray-400">
                      Dentro del grupo expandido, cada sistema tiene un boton <strong className="text-red-400">X</strong> 
                      para removerlo. El sistema volvera a la lista de disponibles.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Guardar Configuracion RF</h3>
                <p className="text-gray-400">
                  Usa el boton <strong className="text-white">"Guardar Config RF"</strong> para capturar el estado actual 
                  de todos tus sistemas (frecuencias, canales, niveles) y asociarlos al evento. Esto es util para:
                </p>
                <ul className="space-y-2 text-gray-400 mt-4">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Recordar configuraciones exitosas para futuros shows</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Documentar setups para giras con multiples fechas</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Compartir configuraciones con otros tecnicos</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Eventos vs Venues</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-blue-500/30">
                    <h4 className="font-semibold text-blue-400 mb-2">Eventos</h4>
                    <p className="text-sm text-gray-400">
                      Para shows especificos con fecha. Ideal para giras y producciones con fecha definida.
                      Incluyen grupos para organizar sistemas.
                    </p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-orange-500/30">
                    <h4 className="font-semibold text-orange-400 mb-2">Venues</h4>
                    <p className="text-sm text-gray-400">
                      Para ubicaciones recurrentes. Ideal para iglesias, teatros o salas que visitas frecuentemente.
                      Guardan configuraciones RF favoritas.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "eventos":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white">Gestion de Eventos</h1>
            <p className="text-gray-400">Organiza tus configuraciones RF por evento o show.</p>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Crear un Evento</h3>
                <ul className="space-y-2 text-gray-400">
                  <li><strong className="text-white">Nombre:</strong> Identificador del evento (ej: "Festival Rock 2026")</li>
                  <li><strong className="text-white">Ubicacion:</strong> Lugar del evento</li>
                  <li><strong className="text-white">Fecha:</strong> Cuando se realizara</li>
                  <li><strong className="text-white">Notas:</strong> Informacion adicional relevante</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Vincular Sistemas</h3>
                <p className="text-gray-400">
                  Puedes asociar tus sistemas In-Ear a eventos especificos para mantener 
                  un historial de configuraciones. Esto es util para giras donde repites venues.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Eventos vs Venues</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg">
                    <h4 className="font-semibold text-blue-400 mb-2">Eventos</h4>
                    <p className="text-sm text-gray-400">
                      Para shows especificos con fecha. Ideal para giras y producciones con fecha definida.
                    </p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg">
                    <h4 className="font-semibold text-orange-400 mb-2">Venues</h4>
                    <p className="text-sm text-gray-400">
                      Para ubicaciones recurrentes. Ideal para iglesias, teatros o salas que visitas frecuentemente.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "hardware":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white">Integracion Hardware</h1>
            <p className="text-gray-400">Conecta tu analizador de espectro RF via USB.</p>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Dispositivos Soportados</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg text-center border border-green-500/20">
                    <Cpu className="w-10 h-10 text-green-400 mx-auto mb-2" />
                    <h4 className="font-semibold text-white">tinySA</h4>
                    <p className="text-xs text-gray-500">Basic / Ultra</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg text-center border border-blue-500/20">
                    <Cpu className="w-10 h-10 text-blue-400 mx-auto mb-2" />
                    <h4 className="font-semibold text-white">RF Explorer</h4>
                    <p className="text-xs text-gray-500">Todas las versiones</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg text-center border border-purple-500/20">
                    <Cpu className="w-10 h-10 text-purple-400 mx-auto mb-2" />
                    <h4 className="font-semibold text-white">Relacart</h4>
                    <p className="text-xs text-gray-500">U.Discover</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-yellow-900/20 border-yellow-700/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-yellow-400 mb-1">Requisitos</h4>
                    <ul className="text-gray-300 text-sm space-y-1">
                      <li>Usar Chrome o Edge (Web Serial API)</li>
                      <li>Abrir la app en pestana nueva (no iframe)</li>
                      <li>Permitir acceso al puerto USB cuando se solicite</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Como Conectar</h3>
                <ol className="space-y-3 text-gray-400">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">1</span>
                    <span>Conecta tu analizador al puerto USB de tu computador</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">2</span>
                    <span>Ve a la seccion <strong className="text-white">"Hardware"</strong> en el menu</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">3</span>
                    <span>Selecciona tu dispositivo en el dropdown</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">4</span>
                    <span>Haz clic en <strong className="text-white">"Conectar"</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">5</span>
                    <span>Selecciona el puerto USB en el dialogo del navegador</span>
                  </li>
                </ol>
              </CardContent>
            </Card>
          </div>
        );

      case "reportes":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white">Reportes PDF</h1>
            <p className="text-gray-400">Genera documentos profesionales con tu configuracion RF.</p>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Tipos de Reporte</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-green-500/20">
                    <h4 className="font-semibold text-green-400 mb-2">Reporte de Evento</h4>
                    <p className="text-sm text-gray-400">
                      PDF completo con visualizacion de espectro, tabla de sistemas, 
                      coordinacion de frecuencias y recomendaciones tecnicas.
                    </p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] rounded-lg border border-blue-500/20">
                    <h4 className="font-semibold text-blue-400 mb-2">Reporte Rapido</h4>
                    <p className="text-sm text-gray-400">
                      PDF simple con todos los sistemas actuales, sin necesidad 
                      de seleccionar un evento. Ideal para documentacion rapida.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Contenido del PDF</h3>
                <ul className="space-y-2 text-gray-400">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Visualizacion de espectro RF</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Tabla de sistemas con frecuencias y estados</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Coordinacion de frecuencias ordenada</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Espaciado entre canales</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Recomendaciones tecnicas</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    <span>Metadata del evento y usuario</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-blue-900/20 border-blue-700/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <FileText className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-blue-400 mb-1">Plan Requerido</h4>
                    <p className="text-gray-300 text-sm">
                      La generacion de reportes PDF esta disponible en los planes <strong>Pro</strong> y <strong>Enterprise</strong>.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "notificaciones":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white">Notificaciones en Tiempo Real</h1>
            <p className="text-gray-400">Alertas instantaneas sobre interferencias y cambios en el espectro RF.</p>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Centro de Notificaciones</h3>
                <p className="text-gray-400">
                  El icono de campana en el menu lateral muestra tus notificaciones. Un punto verde indica 
                  conexion activa con el servidor. Las alertas aparecen en tiempo real.
                </p>
                <div className="space-y-3 mt-4">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1.5 bg-red-500/20 text-red-400 text-sm rounded-full">Interferencia</span>
                    <span className="text-gray-400">Senal detectada en frecuencia de un sistema</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1.5 bg-yellow-500/20 text-yellow-400 text-sm rounded-full">Advertencia</span>
                    <span className="text-gray-400">Posible problema, requiere verificacion</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1.5 bg-blue-500/20 text-blue-400 text-sm rounded-full">Sistema</span>
                    <span className="text-gray-400">Actualizaciones y cambios de configuracion</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#111111] border-white/[0.08]">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-xl font-semibold text-white">Tecnologia WebSocket</h3>
                <p className="text-gray-400">
                  AVX RF PRO utiliza conexiones WebSocket para entregar notificaciones instantaneas. 
                  No necesitas refrescar la pagina para recibir alertas.
                </p>
              </CardContent>
            </Card>
          </div>
        );

      case "suscripciones":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white">Suscripciones</h1>
            <p className="text-gray-400">Planes y precios para profesionales del audio.</p>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="py-3 text-gray-400">Caracteristica</th>
                    <th className="py-3 text-center text-gray-400">Free</th>
                    <th className="py-3 text-center text-green-400">Pro</th>
                    <th className="py-3 text-center text-yellow-400">Enterprise</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-gray-800">
                    <td className="py-3">Precio Mensual</td>
                    <td className="text-center">Gratis</td>
                    <td className="text-center font-bold text-green-400">$25.000 CLP</td>
                    <td className="text-center font-bold text-yellow-400">$68.000 CLP</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3">Precio Anual</td>
                    <td className="text-center">-</td>
                    <td className="text-center text-green-400">-</td>
                    <td className="text-center text-yellow-400">$150.000 CLP</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3">Sistemas IEM</td>
                    <td className="text-center">6</td>
                    <td className="text-center">25</td>
                    <td className="text-center">Ilimitados</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3">Eventos</td>
                    <td className="text-center">2</td>
                    <td className="text-center">15</td>
                    <td className="text-center">Ilimitados</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3">Venues</td>
                    <td className="text-center">3</td>
                    <td className="text-center">15</td>
                    <td className="text-center">Ilimitados</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3">Hardware USB</td>
                    <td className="text-center text-red-400">-</td>
                    <td className="text-center text-green-400">Si</td>
                    <td className="text-center text-green-400">Si</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3">Reportes PDF</td>
                    <td className="text-center text-red-400">-</td>
                    <td className="text-center text-green-400">Si</td>
                    <td className="text-center text-green-400">Si</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3">IA Prediccion RF</td>
                    <td className="text-center text-green-400">Basico</td>
                    <td className="text-center text-green-400">Completo</td>
                    <td className="text-center text-green-400">Completo</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3">Miembros de Equipo</td>
                    <td className="text-center">1</td>
                    <td className="text-center">1</td>
                    <td className="text-center">3-7</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3">Soporte</td>
                    <td className="text-center">Comunidad</td>
                    <td className="text-center">Email</td>
                    <td className="text-center">Prioritario</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Card className="bg-green-900/20 border-green-700/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-green-400 mb-1">Pagos con Flow.cl</h4>
                    <p className="text-gray-300 text-sm">
                      Procesamos pagos de forma segura con Flow.cl. Acepta Webpay, Servipag y Multicaja.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "faq":
        return (
          <div className="space-y-6">
            <h1 className="text-3xl font-bold text-white">Preguntas Frecuentes</h1>
            <p className="text-gray-400">Respuestas a las dudas mas comunes sobre AVX RF PRO.</p>

            <div className="space-y-3">
              {faqs.map((faq, faqIndex) => (
                <Card key={faq.question.substring(0, 30)} className="bg-[#111111] border-white/[0.08] overflow-hidden">
                  <CardContent className="p-0">
                    <button
                      className="w-full p-4 flex items-center justify-between text-left hover:bg-white/[0.02] transition"
                      onClick={() => setExpandedFaq(expandedFaq === faqIndex ? null : faqIndex)}
                    >
                      <span className="font-medium text-white pr-4">{faq.question}</span>
                      {expandedFaq === faqIndex ? (
                        <ChevronDown className="w-5 h-5 text-green-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>
                    {expandedFaq === faqIndex && (
                      <div className="px-4 pb-4 text-gray-400 border-t border-white/[0.05] pt-3">
                        {faq.answer}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Top Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/")}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div className="hidden sm:flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center">
                  <Wifi className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h1 className="text-sm font-bold text-white">AVX RF PRO</h1>
                  <p className="text-[10px] text-gray-500">Manual de Usuario</p>
                </div>
              </div>
            </div>

            <Button
              className="md:hidden"
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>

            <div className="hidden md:block">
              <Button
                onClick={() => navigate("/login")}
                className="bg-green-600 hover:bg-green-700"
              >
                Ir a la App
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-[#0A0A0A] pt-16 md:hidden">
          <div className="p-4 space-y-1 overflow-y-auto h-full pb-20">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  setActiveSection(section.id);
                  setMobileMenuOpen(false);
                }}
                className={`w-full flex items-center justify-between gap-3 p-3 rounded-lg transition ${
                  activeSection === section.id
                    ? "bg-green-900/30 text-green-400"
                    : "text-gray-400 hover:bg-white/5"
                }`}
              >
                <div className="flex items-center gap-3">
                  <section.icon className="w-5 h-5" />
                  <span>{section.title}</span>
                </div>
                {section.badge && (
                  <Badge className="bg-purple-500/20 text-purple-400 text-[10px]">
                    {section.badge}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Main Layout */}
      <div className="pt-16 flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden md:block w-72 fixed left-0 top-16 bottom-0 bg-[#111111] border-r border-white/[0.08] overflow-y-auto">
          <div className="p-4">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Buscar seccion..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#0A0A0A] border-white/[0.08] text-white placeholder:text-gray-500"
              />
            </div>

            <nav className="space-y-1">
              {filteredSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                    activeSection === section.id
                      ? "bg-green-900/30 text-green-400 border border-green-500/30"
                      : "text-gray-400 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <section.icon className="w-4 h-4" />
                    <span>{section.title}</span>
                  </div>
                  {section.badge && (
                    <Badge className="bg-purple-500/20 text-purple-400 text-[9px] px-1.5">
                      {section.badge}
                    </Badge>
                  )}
                </button>
              ))}
            </nav>

            {/* Version Info */}
            <div className="mt-6 pt-4 border-t border-white/[0.08]">
              <div className="text-center text-xs text-gray-600">
                <p>AVX RF PRO v2.0</p>
                <p className="mt-1">Ultima actualizacion: Dic 2025</p>
              </div>
            </div>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 md:ml-72 p-6 md:p-8 max-w-4xl">
          {renderContent()}
          
          {/* Footer Navigation */}
          <div className="mt-12 pt-6 border-t border-white/[0.08]">
            <div className="flex items-center justify-between text-sm">
              <Button
                variant="ghost"
                className="text-gray-400 hover:text-white"
                onClick={() => {
                  const currentIndex = sections.findIndex(s => s.id === activeSection);
                  if (currentIndex > 0) {
                    setActiveSection(sections[currentIndex - 1].id);
                  }
                }}
                disabled={sections.findIndex(s => s.id === activeSection) === 0}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Anterior
              </Button>
              <Button
                variant="ghost"
                className="text-gray-400 hover:text-white"
                onClick={() => {
                  const currentIndex = sections.findIndex(s => s.id === activeSection);
                  if (currentIndex < sections.length - 1) {
                    setActiveSection(sections[currentIndex + 1].id);
                  }
                }}
                disabled={sections.findIndex(s => s.id === activeSection) === sections.length - 1}
              >
                Siguiente
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
